import type {IssueTimelineItemsItemType} from '@github-ui/server-persisted-queries/types/IssueTimelineItems'

import type {TimelineItemTypes} from './fetch-timeline-items'

/**
 * The Activity filter groups timeline item types into categories. Each id is also the alias the
 * `IssueTimelineItemCounts` query uses for that category's count, so the two lists must match.
 * Types the timeline never shows, like mentions, have no category.
 */
export const ACTIVITY_FILTER_CATEGORIES = [
  {
    id: 'statusUpdates',
    label: 'Status updates',
    itemTypes: [
      'CLOSED_EVENT',
      'REOPENED_EVENT',
      'LOCKED_EVENT',
      'UNLOCKED_EVENT',
      'TRANSFERRED_EVENT',
      'CONVERTED_TO_DISCUSSION_EVENT',
      'CONVERTED_FROM_DRAFT_EVENT',
      'MARKED_AS_DUPLICATE_EVENT',
      'UNMARKED_AS_DUPLICATE_EVENT',
    ],
  },
  {id: 'comments', label: 'Comments', itemTypes: ['ISSUE_COMMENT', 'COMMENT_DELETED_EVENT']},
  {
    id: 'crossReferences',
    label: 'Cross-references',
    itemTypes: ['CROSS_REFERENCED_EVENT', 'REFERENCED_EVENT', 'CONNECTED_EVENT', 'DISCONNECTED_EVENT'],
  },
  {
    id: 'relationships',
    label: 'Relationships',
    itemTypes: [
      'PARENT_ISSUE_ADDED_EVENT',
      'PARENT_ISSUE_REMOVED_EVENT',
      'SUB_ISSUE_ADDED_EVENT',
      'SUB_ISSUE_REMOVED_EVENT',
      'BLOCKED_BY_ADDED_EVENT',
      'BLOCKED_BY_REMOVED_EVENT',
      'BLOCKING_ADDED_EVENT',
      'BLOCKING_REMOVED_EVENT',
      'RELATES_TO_ADDED_EVENT',
      'RELATES_TO_REMOVED_EVENT',
    ],
  },
  {id: 'labels', label: 'Label updates', itemTypes: ['LABELED_EVENT', 'UNLABELED_EVENT']},
  {
    id: 'projects',
    label: 'Project updates',
    itemTypes: ['ADDED_TO_PROJECT_V2_EVENT', 'REMOVED_FROM_PROJECT_V2_EVENT', 'PROJECT_V2_ITEM_STATUS_CHANGED_EVENT'],
  },
  {
    id: 'pinned',
    label: 'Pinned',
    itemTypes: ['PINNED_EVENT', 'UNPINNED_EVENT', 'ISSUE_COMMENT_PINNED_EVENT', 'ISSUE_COMMENT_UNPINNED_EVENT'],
  },
  {id: 'assignees', label: 'Assignees', itemTypes: ['ASSIGNED_EVENT', 'UNASSIGNED_EVENT']},
  {id: 'milestones', label: 'Milestones', itemTypes: ['MILESTONED_EVENT', 'DEMILESTONED_EVENT']},
  {
    id: 'titleAndType',
    label: 'Title & type changes',
    itemTypes: [
      'RENAMED_TITLE_EVENT',
      'ISSUE_TYPE_ADDED_EVENT',
      'ISSUE_TYPE_CHANGED_EVENT',
      'ISSUE_TYPE_REMOVED_EVENT',
      'ISSUE_FIELD_ADDED_EVENT',
      'ISSUE_FIELD_CHANGED_EVENT',
      'ISSUE_FIELD_REMOVED_EVENT',
    ],
  },
  {id: 'other', label: 'Other', itemTypes: ['USER_BLOCKED_EVENT']},
] as const satisfies ReadonlyArray<{id: string; label: string; itemTypes: readonly IssueTimelineItemsItemType[]}>

export type ActivityFilterCategory = (typeof ACTIVITY_FILTER_CATEGORIES)[number]['id']

const CATEGORY_IDS: ReadonlySet<string> = new Set(ACTIVITY_FILTER_CATEGORIES.map(category => category.id))

export function isActivityFilterCategory(value: unknown): value is ActivityFilterCategory {
  return typeof value === 'string' && CATEGORY_IDS.has(value)
}

/** In menu order, without duplicates. Also the shape sent with analytics, so combinations compare equal. */
export function normalizeActivityFilters(filters: readonly ActivityFilterCategory[]): ActivityFilterCategory[] {
  return ACTIVITY_FILTER_CATEGORIES.map(category => category.id).filter(id => filters.includes(id))
}

/** No categories, or all of them, reads the whole timeline. */
export function itemTypesForActivityFilters(filters: readonly ActivityFilterCategory[]): TimelineItemTypes | undefined {
  const selected = ACTIVITY_FILTER_CATEGORIES.filter(category => filters.includes(category.id))
  if (selected.length === 0 || selected.length === ACTIVITY_FILTER_CATEGORIES.length) return undefined

  return selected.flatMap(category => category.itemTypes)
}

/** `ProjectV2ItemStatusChangedEvent` -> `PROJECT_V2_ITEM_STATUS_CHANGED_EVENT`, as the server names them. */
export function timelineItemTypeFor(typename: string): IssueTimelineItemsItemType {
  return typename.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase() as IssueTimelineItemsItemType
}
